import React, { useState, useRef } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { parseRotation, uploadTimecard } from '../services/apiService';

// Upload classification options (Phase 1)
const CLASSIFICATION_OPTIONS = [
  { value: 'INITIAL_UPLOAD', label: 'Initial Upload', description: 'First time uploading this rotation' },
  { value: 'REROUTE', label: 'Reroute', description: 'Flight changes from airline' },
  { value: 'EXTENSIVE_DELAYS', label: 'Extensive Delays', description: 'Multiple flights delayed >30 min' },
  { value: 'OTHER_CHANGE', label: 'Other Change', description: 'Hotel, crew, or other modifications' },
  { value: 'POST_AUDIT', label: 'Post-Audit Finding', description: 'End-of-month audit discrepancy' },
  { value: 'AUDIT_CORRECTION', label: 'Audit Correction', description: 'Fixing audit-discovered errors' },
];

const UnifiedUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [activeTab, setActiveTab] = useState('rotation'); // 'rotation' or 'timecard'
  const [inputMethod, setInputMethod] = useState('paste'); // 'paste' or 'file'
  const [textContent, setTextContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadClassification, setUploadClassification] = useState(''); // Phase 1 - blank by default
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validExtensions = ['.ics', '.txt', '.csv'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!validExtensions.includes(fileExt)) {
        setError('Please select a .ics, .txt, or .csv file');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      
      // If it's a text file, read its contents
      if (fileExt === '.txt' || fileExt === '.csv') {
        const reader = new FileReader();
        reader.onload = (event) => {
          setTextContent(event.target.result);
          setInputMethod('paste'); // Switch to paste view to show content
        };
        reader.readAsText(file);
      }
    }
  };

  const handleUpload = async () => {
    setError('');
    setSuccess('');
    
    // Validate input
    if (inputMethod === 'paste' && !textContent?.trim()) {
      setError('Please paste or enter data before uploading');
      return;
    }
    if (inputMethod === 'file' && !selectedFile) {
      setError('Please select a file before uploading');
      return;
    }

    setIsProcessing(true);

    try {
      let result;
      
      if (activeTab === 'rotation') {
        // Parse rotation text with classification (Phase 1)
        const text = inputMethod === 'file' && selectedFile?.name?.endsWith('.ics') 
          ? await selectedFile.text() 
          : textContent;
        result = await parseRotation(text, {
          assignment_type: 'Line Holder',
          upload_classification: uploadClassification, // Phase 1
        });
        setSuccess(`Rotation parsed successfully! ${result?.rotation_id ? `ID: ${result.rotation_id}` : ''}`);
      } else {
        // Upload timecard
        if (inputMethod === 'file' && selectedFile?.name?.endsWith('.ics')) {
          result = await uploadTimecard(selectedFile);
        } else {
          // For text content, create a blob and upload
          const blob = new Blob([textContent], { type: 'text/plain' });
          const file = new File([blob], 'timecard.txt', { type: 'text/plain' });
          result = await uploadTimecard(file);
        }
        setSuccess('Timecard uploaded successfully!');
      }
      
      // Clear form after success
      setTimeout(() => {
        setTextContent('');
        setSelectedFile(null);
        setUploadClassification(''); // Phase 1: reset to blank
        setSuccess('');
        if (onUploadSuccess) {
          onUploadSuccess(result);
        }
        onClose();
      }, 1500);
      
    } catch (err) {
      console.error('❌ UPLOAD FAILED - Full Error:', err);
      console.error('📊 Error Response Data:', err?.response?.data);
      console.error('📄 Error Response Status:', err?.response?.status);
      console.error('🔍 Error Response Headers:', err?.response?.headers);
      console.error('💬 Error Message:', err?.message);
      
      // Handle error - ensure we always set a string
      const errorDetail = err?.response?.data?.detail;
      let errorMessage = 'Failed to process. Please check the format and try again.';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (errorDetail?.error) {
        // API returns {error: string, type: string}
        errorMessage = errorDetail.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setTextContent('');
      setSelectedFile(null);
      setError('');
      setSuccess('');
      setUploadClassification(''); // Phase 1: reset to blank
      setActiveTab('rotation');
      setInputMethod('paste');
      onClose();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const input = { target: { files: [file] } };
      handleFileSelect(input);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative w-full max-w-2xl card p-6 md:p-8 max-h-[90vh] overflow-y-auto glow-prominent">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent-cyan/10 border border-accent-cyan/20">
              <Icon name="Upload" size={24} color="var(--color-accent-cyan)" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Upload Data
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Import rotation or timecard data
              </p>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="p-2 rounded-lg hover:bg-tertiary transition-smooth disabled:opacity-50"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-2 mb-6 p-1 bg-tertiary rounded-xl">
          <button
            onClick={() => setActiveTab('rotation')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-smooth ${
              activeTab === 'rotation'
                ? 'bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/30'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Plane" size={18} />
            <span>Rotation</span>
          </button>
          <button
            onClick={() => setActiveTab('timecard')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-smooth ${
              activeTab === 'timecard'
                ? 'bg-accent-purple/20 text-accent-purple border border-accent-purple/30'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="FileText" size={18} />
            <span>Timecard</span>
          </button>
        </div>

        {/* Input Method Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setInputMethod('paste')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-smooth ${
              inputMethod === 'paste'
                ? 'bg-secondary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Clipboard" size={16} />
            <span>Paste Text</span>
          </button>
          <button
            onClick={() => setInputMethod('file')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-smooth ${
              inputMethod === 'file'
                ? 'bg-secondary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="File" size={16} />
            <span>Upload File</span>
          </button>
        </div>

        {/* Content Area */}
        <div className="space-y-4">
          {inputMethod === 'paste' ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {activeTab === 'rotation' ? 'Rotation Data' : 'Timecard Data'}
              </label>
              <textarea
                value={textContent}
                onChange={(e) => {
                  setTextContent(e?.target?.value);
                  setError('');
                }}
                placeholder={activeTab === 'rotation' 
                  ? "Paste your rotation data here...\n\nSupported formats:\n• PBS rotation text\n• Crew schedule exports\n• ICS calendar content"
                  : "Paste your timecard data here...\n\nSupported formats:\n• Company timecard exports\n• Pay period summaries"
                }
                disabled={isProcessing}
                className="w-full h-56 px-4 py-3 bg-tertiary border border-accent-cyan/20 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent-cyan/60 focus:ring-2 focus:ring-accent-cyan/20 transition-smooth resize-none font-data text-sm disabled:opacity-50"
              />
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className={`h-56 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-4 cursor-pointer transition-smooth ${
                selectedFile 
                  ? 'border-success/50 bg-success/5' 
                  : 'border-accent-cyan/30 hover:border-accent-cyan/60 bg-tertiary/50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".ics,.txt,.csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {selectedFile ? (
                <>
                  <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-success/10 border border-success/20">
                    <Icon name="FileCheck" size={32} color="var(--color-success)" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-foreground">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="text-sm text-muted-foreground hover:text-error transition-smooth"
                  >
                    Remove file
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-accent-cyan/10 border border-accent-cyan/20">
                    <Icon name="Upload" size={32} color="var(--color-accent-cyan)" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-foreground">Drop file here or click to browse</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Supports .ics, .txt, .csv files
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Status Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
              <Icon name="AlertCircle" size={16} />
              <span>{error}</span>
            </div>
          )}
          
          {success && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/20 text-success text-sm">
              <Icon name="CheckCircle" size={16} />
              <span>{success}</span>
            </div>
          )}

          {/* Phase 1: Upload Classification (Rotation only) */}
          {activeTab === 'rotation' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Classification <span className="text-accent-cyan text-xs font-normal ml-1">(Phase 1)</span>
              </label>
              <select
                value={uploadClassification}
                onChange={(e) => setUploadClassification(e.target.value)}
                disabled={isProcessing}
                className={`w-full px-4 py-2.5 bg-tertiary border rounded-xl text-foreground focus:outline-none focus:border-accent-cyan/60 focus:ring-2 focus:ring-accent-cyan/20 transition-smooth disabled:opacity-50 appearance-none cursor-pointer ${
                  uploadClassification === '' 
                    ? 'border-error/40 text-muted-foreground' 
                    : 'border-accent-cyan/20'
                }`}
              >
                <option value="" disabled>
                  ⚠️ Select a classification reason...
                </option>
                {CLASSIFICATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} — {option.description}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1.5">
                Help us track upload types for better data management. This affects rate limiting and audit trails.
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className={`rounded-xl p-4 border ${
            activeTab === 'rotation' 
              ? 'bg-accent-cyan/5 border-accent-cyan/20' 
              : 'bg-accent-purple/5 border-accent-purple/20'
          }`}>
            <div className="flex items-start gap-3">
              <Icon 
                name="Info" 
                size={20} 
                color={activeTab === 'rotation' ? 'var(--color-accent-cyan)' : 'var(--color-accent-purple)'} 
                className="flex-shrink-0 mt-0.5" 
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground mb-2">
                  {activeTab === 'rotation' ? 'Rotation Upload Tips' : 'Timecard Upload Tips'}
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {activeTab === 'rotation' ? (
                    <>
                      <li>• Copy/paste from PBS or crew scheduling system</li>
                      <li>• Upload .ics calendar files from AIMS or similar</li>
                      <li>• Text files with rotation details work too</li>
                      <li>• Flight times, routes, and duty periods are auto-parsed</li>
                    </>
                  ) : (
                    <>
                      <li>• Upload company timecard exports</li>
                      <li>• ICS files with pay period data</li>
                      <li>• Timecards are matched to rotations automatically</li>
                      <li>• Discrepancies are flagged for review</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isProcessing}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleUpload}
              loading={isProcessing}
              iconName="Upload"
              iconPosition="left"
              className="flex-1 sm:flex-none"
              disabled={isProcessing || (inputMethod === 'paste' ? !textContent?.trim() : !selectedFile) || (activeTab === 'rotation' && !uploadClassification)}
            >
              {isProcessing ? 'Processing...' : `Upload ${activeTab === 'rotation' ? 'Rotation' : 'Timecard'}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedUploadModal;
